import React, { useState, useRef } from 'react';
import Layout from '../components/Layout';
import { jsPDF } from 'jspdf';
import { sanitizeInput } from '../utils/security';
import { processInventoryData } from '../utils/api-mock';
import '../styles/pricing-calculator.css';
import '../styles/inventory-optimization.css';
import '../styles/service-fix.css'; // Fix for dark text on dark background

const InventoryOptimization = () => {
  const [formData, setFormData] = useState({
    warehouseCount: 12,
    skuCount: '',
    historicalDataYears: 2,
    seasonalFactors: false,
    promotionalEvents: false,
    leadTimeData: false,
    forecastHorizon: 6
  });
  
  const [results, setResults] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [fileUploadStatus, setFileUploadStatus] = useState('');
  const [fileUploading, setFileUploading] = useState(false);
  const resultsRef = useRef(null);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleFileUpload = async (event) => {
    const selectedFile = event.target.files[0];
    if (!selectedFile) return;
    
    // Define allowed file extensions
    const validExtensions = ['csv', 'xlsx', 'xls'];
    
    // Validate file type and size
    const fileExtension = selectedFile.name.split('.').pop().toLowerCase();
    if (!validExtensions.includes(fileExtension)) {
      setFileUploadStatus('Invalid file type. Please upload CSV, XLSX, or XLS files.');
      return;
    }
    
    if (selectedFile.size > 10 * 1024 * 1024) { // 10MB limit
      setFileUploadStatus('File too large. Maximum size is 10MB.');
      return;
    }
    
    setFileUploading(true);
    setFileUploadStatus(`Processing: ${selectedFile.name}`);
    
    // Simulate file processing
    setTimeout(() => {
      setFileUploadStatus(`Successfully uploaded: ${sanitizeInput(selectedFile.name)}`);
      setFileUploading(false);
    }, 2000);
  };

  const generateReport = async () => {
    setIsLoading(true);
    
    try {
      // Call the mock API function
      const data = await processInventoryData(formData);
      setResults(data);
    } catch (error) {
      console.error('Error generating report:', error);
      alert('Failed to generate report. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const downloadPdf = () => {
    if (!results) return;
    
    try {
      // Create PDF document
      const pdf = new jsPDF();
      
      // PDF configuration
      const margin = 15;
      
      // Add title
      pdf.setFontSize(20);
      pdf.setFont(undefined, 'bold');
      pdf.text('Inventory Optimization Report', margin, 20);
      
      // Add summary section
      pdf.setFontSize(16);
      pdf.text('Summary', margin, 40);
      
      pdf.setFontSize(12);
      pdf.setFont(undefined, 'normal');
      pdf.text(`Total SKUs: ${results.summary.totalSkus}`, margin, 55);
      pdf.text(`Warehouses: ${results.summary.warehouseCount}`, margin, 65);
      pdf.text(`Forecast Accuracy: ${results.summary.forecastAccuracy}`, margin, 75);
      pdf.text(`Potential Annual Savings: ${results.summary.potentialSavings}`, margin, 85);
      pdf.text(`Stockout Reduction: ${results.summary.stockoutReduction}`, margin, 95);
      
      // Add recommendations section
      pdf.setFontSize(16);
      pdf.setFont(undefined, 'bold');
      pdf.text('High Priority Recommendations', margin, 115);
      
      // Add table headers
      pdf.setFontSize(12);
      pdf.setFont(undefined, 'bold');
      pdf.text('SKU', margin, 130);
      pdf.text('Warehouse', margin + 40, 130);
      pdf.text('Reorder Point', margin + 80, 130);
      pdf.text('EOQ', margin + 130, 130);
      pdf.text('Safety Stock', margin + 160, 130);
      
      // Add table rows
      pdf.setFont(undefined, 'normal');
      results.recommendations.highPriority.forEach((item, index) => {
        const y = 140 + (index * 10);
        pdf.text(item.sku, margin, y);
        pdf.text(item.warehouse, margin + 40, y);
        pdf.text(item.reorderPoint.toString(), margin + 80, y);
        pdf.text(item.eoq.toString(), margin + 130, y);
        pdf.text(item.safetyStock.toString(), margin + 160, y);
      });
      
      // Add seasonal adjustments section
      pdf.setFontSize(16);
      pdf.setFont(undefined, 'bold');
      pdf.text('Seasonal Adjustments', margin, 180);
      
      // Add table headers
      pdf.setFontSize(12);
      pdf.setFont(undefined, 'bold');
      pdf.text('Season', margin, 195);
      pdf.text('SKU Group', margin + 40, 195);
      pdf.text('Adjustment Factor', margin + 100, 195);
      
      // Add table rows
      pdf.setFont(undefined, 'normal');
      results.recommendations.seasonalAdjustments.forEach((item, index) => {
        const y = 205 + (index * 10);
        pdf.text(item.season, margin, y);
        pdf.text(item.skuGroup, margin + 40, y);
        pdf.text(item.adjustmentFactor.toString(), margin + 100, y);
      });
      
      // Save the PDF
      pdf.save('inventory-optimization-report.pdf');
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again later.');
    }
  };

  return (
    <Layout>
      <div className="pricing-calculator-page">
        <div className="hero-section">
          <h1>Retail Inventory Optimization</h1>
          <p className="hero-subtitle">
            Reduce excess inventory costs and avoid stockouts with AI-powered demand forecasting and inventory optimization
          </p>
        </div>
        
        <div className="chat-container" style={{ height: 'auto', padding: '20px' }}>
          <h2>Input Parameters</h2>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
            <div>
              <label htmlFor="warehouseCount" style={{ display: 'block', marginBottom: '5px' }}>
                Number of Warehouses:
              </label>
              <input
                type="number"
                id="warehouseCount"
                name="warehouseCount"
                value={formData.warehouseCount}
                onChange={handleInputChange}
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
              />
            </div>
            
            <div>
              <label htmlFor="skuCount" style={{ display: 'block', marginBottom: '5px' }}>
                Number of SKUs:
              </label>
              <input
                type="number"
                id="skuCount"
                name="skuCount"
                value={formData.skuCount}
                onChange={handleInputChange}
                placeholder="e.g. 1000"
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
              />
            </div>
            
            <div>
              <label htmlFor="historicalDataYears" style={{ display: 'block', marginBottom: '5px' }}>
                Years of Historical Data:
              </label>
              <input
                type="number"
                id="historicalDataYears"
                name="historicalDataYears"
                value={formData.historicalDataYears}
                onChange={handleInputChange}
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
              />
            </div>
            
            <div>
              <label htmlFor="forecastHorizon" style={{ display: 'block', marginBottom: '5px' }}>
                Forecast Horizon (months):
              </label>
              <input
                type="number"
                id="forecastHorizon"
                name="forecastHorizon"
                value={formData.forecastHorizon}
                onChange={handleInputChange}
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
              />
            </div>
          </div>
          
          <div style={{ marginBottom: '20px' }}>
            <h3>Available Data</h3>
            <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  name="seasonalFactors"
                  checked={formData.seasonalFactors}
                  onChange={handleInputChange}
                  style={{ marginRight: '8px' }}
                />
                Seasonal Factors
              </label>
              
              <label style={{ display: 'flex', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  name="promotionalEvents"
                  checked={formData.promotionalEvents}
                  onChange={handleInputChange}
                  style={{ marginRight: '8px' }}
                />
                Promotional Events
              </label>
              
              <label style={{ display: 'flex', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  name="leadTimeData"
                  checked={formData.leadTimeData}
                  onChange={handleInputChange}
                  style={{ marginRight: '8px' }}
                />
                Supplier Lead Times
              </label>
            </div>
          </div>
          
          <div style={{ marginBottom: '20px' }}>
            <h3>Upload Historical Data</h3>
            <div className="file-upload-container">
              <label className="file-upload-label">
                <span className="file-upload-button">
                  Upload Data File
                </span>
                <input
                  type="file"
                  onChange={handleFileUpload}
                  disabled={fileUploading}
                  accept=".csv,.xlsx,.xls"
                  style={{ display: 'none' }}
                />
              </label>
              <p className="file-help-text">
                Upload CSV or Excel files with historical sales, returns, and stock data (max 10MB)
              </p>
              {fileUploadStatus && (
                <div className="file-status">{fileUploadStatus}</div>
              )}
            </div>
          </div>
          
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
            <button
              onClick={generateReport}
              disabled={isLoading}
              style={{
                backgroundColor: '#1e3c72',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '4px',
                fontSize: '1rem',
                cursor: 'pointer'
              }}
            >
              {isLoading ? 'Generating...' : 'Generate Optimization Report'}
            </button>
          </div>
          
          {results && (
            <div ref={resultsRef} style={{ marginTop: '40px' }}>
              <h2>Optimization Results</h2>
              
              <div style={{ background: '#f9f9f9', padding: '20px', borderRadius: '8px', marginBottom: '20px' }}>
                <h3>Summary</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '15px' }}>
                  <div>
                    <strong>Total SKUs:</strong> {results.summary.totalSkus}
                  </div>
                  <div>
                    <strong>Warehouses:</strong> {results.summary.warehouseCount}
                  </div>
                  <div>
                    <strong>Forecast Accuracy:</strong> {results.summary.forecastAccuracy}
                  </div>
                  <div>
                    <strong>Potential Savings:</strong> {results.summary.potentialSavings}
                  </div>
                  <div>
                    <strong>Stockout Reduction:</strong> {results.summary.stockoutReduction}
                  </div>
                </div>
              </div>
              
              <div style={{ marginBottom: '30px' }}>
                <h3>High Priority Recommendations</h3>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f0f4f8' }}>
                        <th style={{ padding: '10px', textAlign: 'left', border: '1px solid #ddd' }}>SKU</th>
                        <th style={{ padding: '10px', textAlign: 'left', border: '1px solid #ddd' }}>Warehouse</th>
                        <th style={{ padding: '10px', textAlign: 'left', border: '1px solid #ddd' }}>Reorder Point</th>
                        <th style={{ padding: '10px', textAlign: 'left', border: '1px solid #ddd' }}>EOQ</th>
                        <th style={{ padding: '10px', textAlign: 'left', border: '1px solid #ddd' }}>Safety Stock</th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.recommendations.highPriority.map((item, index) => (
                        <tr key={index}>
                          <td style={{ padding: '10px', border: '1px solid #ddd' }}>{item.sku}</td>
                          <td style={{ padding: '10px', border: '1px solid #ddd' }}>{item.warehouse}</td>
                          <td style={{ padding: '10px', border: '1px solid #ddd' }}>{item.reorderPoint}</td>
                          <td style={{ padding: '10px', border: '1px solid #ddd' }}>{item.eoq}</td>
                          <td style={{ padding: '10px', border: '1px solid #ddd' }}>{item.safetyStock}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              
              <div style={{ marginBottom: '30px' }}>
                <h3>Seasonal Adjustments</h3>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f0f4f8' }}>
                        <th style={{ padding: '10px', textAlign: 'left', border: '1px solid #ddd' }}>Season</th>
                        <th style={{ padding: '10px', textAlign: 'left', border: '1px solid #ddd' }}>SKU Group</th>
                        <th style={{ padding: '10px', textAlign: 'left', border: '1px solid #ddd' }}>Adjustment Factor</th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.recommendations.seasonalAdjustments.map((item, index) => (
                        <tr key={index}>
                          <td style={{ padding: '10px', border: '1px solid #ddd' }}>{item.season}</td>
                          <td style={{ padding: '10px', border: '1px solid #ddd' }}>{item.skuGroup}</td>
                          <td style={{ padding: '10px', border: '1px solid #ddd' }}>{item.adjustmentFactor}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
                <button
                  onClick={downloadPdf}
                  style={{
                    backgroundColor: '#c62828',
                    color: 'white',
                    border: 'none',
                    padding: '12px 24px',
                    borderRadius: '4px',
                    fontSize: '1rem',
                    cursor: 'pointer'
                  }}
                >
                  Download Report as PDF
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default InventoryOptimization;